// bd-deepcontext-zgbz — one `<tr>` of the jobs table. The page lock retired the
// fat multi-line row (lists-are-tables): the Job cell stacks name + blueprint
// pill(s) + a muted meta line, and everything else that used to be crammed onto
// those two lines now has its own column. Behaviour is unchanged — the same
// handlers, titles and admin gating as before.
import {
  ChartBarIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  PlayIcon,
  PowerIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { ActionIconButton } from "../ui/ActionIconButton";
import { Spinner } from "../ui/spinner";
import { TableRow, TableCell } from "../ui/table";
import { formatDateUTC, rel, relFuture, shortDate } from "./formatters";
import { calculateJobDailyStats } from "./jobMetrics";
import { isJobExpired } from "./jobStatus";
import { grouped } from "./numberFormat";
import { describeLocalModel } from "./localModelAccess";
import type { LocalModelServer, ScheduledJob } from "./types";

interface JobRowProps {
  job: ScheduledJob;
  isAdmin: boolean;
  /** deepcontext-63k8.3 — configured self-hosted servers; set only for the
   *  allowed user, and then jobs on a server get a tag naming it. */
  localModels?: LocalModelServer[];
  blueprintNames: Record<string, string>;
  onShowRuns: (job: ScheduledJob) => void;
  onEdit: (job: ScheduledJob) => void;
  onToggleActive: (job: ScheduledJob) => void;
  onExecute: (job: ScheduledJob) => void;
  onDelete: (job: ScheduledJob) => void;
  onDuplicate: (job: ScheduledJob) => void;
  onGoToBlueprint: (promptId: string) => void;
}

/** bd-2c2pk — the compact active window, full UTC bounds on hover. */
const activeWindow = (job: ScheduledJob): string | null => {
  if (!job.startDate) return null;
  return job.endDate
    ? `Active ${shortDate(job.startDate)} → ${shortDate(job.endDate)}`
    : `Active from ${shortDate(job.startDate)}`;
};

const activeWindowTitle = (job: ScheduledJob): string | undefined => {
  if (!job.startDate) return undefined;
  return job.endDate
    ? `Active from: ${formatDateUTC(job.startDate)} · Until: ${formatDateUTC(job.endDate)}`
    : `Active from: ${formatDateUTC(job.startDate)}`;
};

const JobRow: React.FC<JobRowProps> = ({
  job,
  isAdmin,
  localModels,
  blueprintNames,
  onShowRuns,
  onEdit,
  onToggleActive,
  onExecute,
  onDelete,
  onDuplicate,
  onGoToBlueprint,
}) => {
  const dailyStats = calculateJobDailyStats(job);
  const expired = isJobExpired(job);
  const dormant = expired || !job.isActive;
  const localTag =
    localModels && job.localModelId
      ? describeLocalModel(job.localModelId, localModels)
      : null;

  const meta = [job.siteListId ? "Site List" : null, activeWindow(job)]
    .filter(Boolean)
    .join(" · ");

  // Schedule cell: bold next run over the muted last run (bd-2c2pk relative
  // times, full UTC timestamps on hover). While a run is in flight (deepcontext-ikwv)
  // the next-run line is replaced by a spinner + "Running now" — the last-run
  // line underneath is unaffected.
  const nextLine =
    !dormant && job.nextRunAt ? `next ${relFuture(job.nextRunAt)}` : "—";
  const lastLine = expired && job.endDate
    ? `ended ${shortDate(job.endDate)}`
    : job.lastRunAt
      ? `ran ${rel(job.lastRunAt)} ago`
      : "never run";
  const scheduleTitle =
    [
      job.lastRunAt && `Last run: ${formatDateUTC(job.lastRunAt)}`,
      job.nextRunAt && `Next run: ${formatDateUTC(job.nextRunAt)}`,
    ]
      .filter(Boolean)
      .join(" · ") || undefined;

  return (
    <TableRow className={dormant ? "opacity-70" : ""}>
      <TableCell className="align-middle">
        {/* Clicking the title opens the same Edit modal as the pencil action
            button, mirroring the Brand Blueprints title affordance. Only admins
            may edit (bd-deepcontext-dnjks); others see plain text. */}
        {isAdmin ? (
          <button
            type="button"
            onClick={() => onEdit(job)}
            className="block text-left font-extrabold text-accent-active hover:underline focus:outline-none focus:ring-2 focus:ring-accent rounded"
            aria-label={`Edit ${job.name}`}
          >
            {job.name}
          </button>
        ) : (
          <span className="block font-extrabold text-accent-active">
            {job.name}
          </span>
        )}
        {/* deepcontext-63k8.3 — scored by a self-hosted server, not Gemini. */}
        {localTag && (
          <span
            data-testid="job-local-model-tag"
            title={
              localTag.configured
                ? "Runs on this self-hosted model"
                : "This self-hosted server is no longer configured"
            }
            className={`mt-1 inline-block rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide bg-ghost-bg ${
              localTag.configured ? "text-ink-muted" : "text-warn"
            }`}
          >
            {localTag.text}
          </span>
        )}
        {/* bd-uwc1x / bd-wxgk9 — connected blueprint pill(s). Clicking
            deep-links to /prompts filtered to that blueprint's ID. */}
        {job.promptIds && job.promptIds.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-1">
            {job.promptIds.map((promptId) => (
              <button
                key={promptId}
                type="button"
                onClick={() => onGoToBlueprint(promptId)}
                title="View this brand blueprint"
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-bold transition-opacity hover:opacity-80 ${
                  blueprintNames[promptId]
                    ? "bg-safe-bg text-safe"
                    : "bg-ghost-bg text-ink-muted"
                }`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-safe" aria-hidden />
                <span className="truncate max-w-[12rem]">
                  {/* bd-5gpue — names are eager-loaded before first paint;
                      fall back to the id for any unresolved blueprint. */}
                  {blueprintNames[promptId] || promptId}
                </span>
              </button>
            ))}
          </span>
        )}
        {meta && (
          <span
            className="mt-0.5 block text-xs text-ink-muted"
            title={activeWindowTitle(job)}
          >
            {meta}
          </span>
        )}
      </TableCell>

      <TableCell className="align-middle">
        <span
          className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold ${
            job.urlSourceMode === "classification"
              ? "bg-nav-active text-accent-active"
              : "bg-ghost-bg text-ink-muted"
          }`}
        >
          {job.urlSourceMode === "classification" ? "Classification" : "Default"}
        </span>
      </TableCell>

      <TableCell className="align-middle whitespace-nowrap tabular-nums">
        {grouped(job.urlsPerRun)} × {job.runsPerDay}/day
      </TableCell>

      <TableCell className="align-middle whitespace-nowrap tabular-nums">
        {dormant ? "—" : `~$${dailyStats.estimatedCost.toFixed(2)}`}
      </TableCell>

      <TableCell className="align-middle whitespace-nowrap" title={scheduleTitle}>
        {job.hasRunningJob ? (
          <span className="flex items-center gap-1.5 text-xs font-bold text-accent-active">
            <Spinner size="sm" />
            Running now
          </span>
        ) : (
          <span className="block text-xs font-bold text-ink">{nextLine}</span>
        )}
        <span className="block text-xs text-ink-muted">{lastLine}</span>
      </TableCell>

      <TableCell className="align-middle">
        {/* deepcontext-c919.2 — 3x2 grid (row 1: View Runs / Active toggle /
            Execute Now, row 2: Edit / Duplicate / Delete) so Duplicate fits
            without widening the column. Non-admins only ever see the single
            View Runs button, so the grid degrades to one cell for them. */}
        <div className="grid grid-cols-3 gap-0.5 justify-items-end ml-auto w-fit">
          <ActionIconButton
            kind="panel"
            onClick={() => onShowRuns(job)}
            title="View Runs"
          >
            <ChartBarIcon className="h-3.5 w-3.5" />
          </ActionIconButton>
          {isAdmin && (
            <>
              <ActionIconButton
                kind="active-toggle"
                active={job.isActive}
                onClick={() => onToggleActive(job)}
                title={
                  (job.isActive ? "Deactivate" : "Activate") +
                  (job.autoSyncManaged
                    ? " — currently auto-synced from linked DSP campaigns (updated approximately every 3 hours). The job stays active as long as at least one linked campaign is active. Manual toggle here turns off auto-sync."
                    : "")
                }
              >
                <PowerIcon className="h-3.5 w-3.5" />
              </ActionIconButton>
              <ActionIconButton
                kind="run"
                onClick={() => onExecute(job)}
                disabled={job.hasRunningJob}
                className={job.hasRunningJob ? "opacity-40 cursor-not-allowed" : ""}
                title={
                  job.hasRunningJob ? "Job is currently running" : "Execute Now"
                }
              >
                <PlayIcon className="h-3.5 w-3.5" />
              </ActionIconButton>
              <ActionIconButton
                kind="edit"
                onClick={() => onEdit(job)}
                title="Edit"
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </ActionIconButton>
              <ActionIconButton
                kind="panel"
                onClick={() => onDuplicate(job)}
                title="Duplicate"
              >
                <DocumentDuplicateIcon className="h-3.5 w-3.5" />
              </ActionIconButton>
              <ActionIconButton
                kind="delete"
                onClick={() => onDelete(job)}
                title="Delete"
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </ActionIconButton>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default JobRow;
